import React, { useState, useMemo } from 'react'
import { Box, Card, Flex, Grid, Text, Icon, Tooltip, Button, useToast, VStack } from '@chakra-ui/react'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import MasterTable, { Column, RowData } from '../../../components/tables/base/MasterTable'
import SearchInput from '../../../components/tables/base/SearchInput'
import FilterButton from '../../../components/tables/base/FilterButton'
import Pagination from '../../../components/tables/base/Pagination'
import LoadingTable from '../../../components/tables/base/LoadingTable'
import formatDate from '../../../helpers/formatDate'
import {
  fetchBenefitChangeLogs,
  fetchEmployeeList,
  exportBenefitChangeLogsToCsv,
  exportBenefitChangeLogsToXls,
} from '../../../api/workforce/workforce'
import { useTranslation } from 'react-i18next'
import { Plus, Edit3, Trash2, ArrowRight, PiggyBank, Heart, GraduationCap, Car, Plane, DollarSign, Umbrella, Package } from 'lucide-react'
import { CircleFlag } from 'react-circle-flags'
import { getCountryNameByCode } from '../../../components/countries/PhoneDial'
import AvatarCell from '../../../components/tables/base/AvatarCell'
import { ExportIcon } from '../../../assets/icons'
import handleToast from '../../../components/HandleToast'

type LucideIcon = React.FC<React.SVGProps<SVGSVGElement>>

const BENEFIT_TYPE_CONFIG: Record<string, { bg: string; color: string; icon: LucideIcon }> = {
  pension: {
    bg: '#A5E8D3',
    color: '#065F46',
    icon: PiggyBank,
  },
  health: {
    bg: '#FCA5A5',
    color: '#991B1B',
    icon: Heart,
  },
  education: {
    bg: '#DDD6FE',
    color: '#5B21B6',
    icon: GraduationCap,
  },
  vehicle: {
    bg: '#BFDBFE',
    color: '#1E40AF',
    icon: Car,
  },
  travel_allowance: {
    bg: '#CCFBF1',
    color: '#0F766E',
    icon: Plane,
  },
  extra_month_salary: {
    bg: '#FEF3C7',
    color: '#92400E',
    icon: DollarSign,
  },
  vacation_allowance: {
    bg: '#E0E7FF',
    color: '#3730A3',
    icon: Umbrella,
  },
  other: {
    bg: '#E5E7EB',
    color: '#374151',
    icon: Package,
  },
}

const BENEFIT_TYPE_FALLBACK: Record<string, string> = {
  pension: 'Pension',
  health: 'Health',
  education: 'Education',
  vehicle: 'Vehicle',
  travel_allowance: 'Travel allowance',
  extra_month_salary: 'Extra month salary',
  vacation_allowance: 'Vacation allowance',
  other: 'Other',
}


const ACTION_BADGE_CONFIG = {
  create: { bg: '#D1FAE5', color: '#065F46', borderColor: '#059669' },
  update: { bg: '#DBEAFE', color: '#1E40AF', borderColor: '#3B82F6' },
  delete: { bg: '#FEE2E2', color: '#991B1B', borderColor: '#DC2626' },
  default: { bg: '#F3F4F6', color: '#374151', borderColor: '#D1D5DB' },
}

const CHANGE_SUMMARY_BADGE = {
  create: { bg: '#F0FDF4', iconColor: '#059669', textColor: '#065F46' },
  delete: { bg: '#FEF2F2', iconColor: '#DC2626', textColor: '#991B1B' },
}

/** Single change log entry from API */
interface BenefitChangeLogEntry {
  id: string
  benefit: string | {
    id: string
    type?: string
    value?: string
    currency?: string
    frequency?: string
    description?: string
    employee?: {
      id: string
      first_name: string
      last_name: string
      profile_picture?: string
    } | null
    team?: {
      id: string
      name: string
    } | null
    country_of_work?: string | null
  }
  action: 'create' | 'update' | 'delete'
  changed_by: string | {
    id: string
    first_name: string
    last_name: string
    profile_picture?: string
  }
  field_name?: string
  previous_value: string | null
  new_value: string | null
  created_at: string
}





const BenefitMutationsPage: React.FC = () => {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const itemsPerPage = 15
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const toast = useToast()
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)      

  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // --- Filter: Employee (benefit assignee) - infinite scroll ---
  const [employeeFilterSearch, setEmployeeFilterSearch] = useState('')
  const employeeFilterQuery = useInfiniteQuery({
    queryKey: ['filter-employees', 10, employeeFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: employeeFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    staleTime: 30000,
  })
  const employeeFilterList = useMemo(
    () =>
      (employeeFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: unknown; first_name?: string; last_name?: string; work_email?: string }[] }) =>
        (p.data ?? []).map((e) => ({
          id: String(e.id),
          name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.work_email || String(e.id),
        }))
      ),
    [employeeFilterQuery.data]
  )

  // --- Filter: Changed By - infinite scroll ---
  const [changedByFilterSearch, setChangedByFilterSearch] = useState('')
  const changedByFilterQuery = useInfiniteQuery({
    queryKey: ['filter-changed-by', 10, changedByFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({ search: changedByFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    staleTime: 30000,
  })
  const changedByFilterList = useMemo(
    () =>
      (changedByFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: unknown; first_name?: string; last_name?: string; work_email?: string }[] }) =>
        (p.data ?? []).map((e) => ({
          id: String(e.id),
          name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.work_email || String(e.id),
        }))
      ),
    [changedByFilterQuery.data]
  )

  // --- Main query ---
  const mutationsQuery = useQuery({
    queryKey: ['benefit-mutations', page, itemsPerPage, searchTerm, filters],
    queryFn: () =>
      fetchBenefitChangeLogs({
        page,
        page_size: itemsPerPage,
        search: searchTerm || undefined,
        employee: filters.employee?.length ? filters.employee[0] : undefined,
        changed_by: filters.changed_by?.length ? filters.changed_by[0] : undefined,
      }),
    placeholderData: (prev) => prev,
  })

  // Handle both response shapes: { data: [...], count, max_page } or direct array [...]
  const mutationsList: BenefitChangeLogEntry[] = useMemo(() => {
    const raw = mutationsQuery.data
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw.data)) return raw.data
    if (Array.isArray(raw.results)) return raw.results
    return []
  }, [mutationsQuery.data])

    const exportMutationsToXls = async () => {
  try {
    setIsExporting(true)

    await exportBenefitChangeLogsToXls({
      search: searchTerm || undefined,
      employee: filters.employee?.length ? filters.employee[0] : undefined,
      changed_by: filters.changed_by?.length ? filters.changed_by[0] : undefined,
    })

    handleToast(
      toast,
      true,
      t('workforce.messages.exportSuccess'),
      t('workforce.messages.exportSuccessMessage', { format: 'XLS' }),
    )
  } catch (e) {
    console.error(e)
    handleToast(
      toast,
      false,
      t('workforce.messages.exportFailed'),
      t('workforce.messages.exportFailedMessage'),
    )
  } finally {
    setIsExporting(false)
    setIsExportDropdownOpen(false)
  }
}

const exportMutationsToCsv = async () => {
  try {
    setIsExporting(true)

    await exportBenefitChangeLogsToCsv({
      search: searchTerm || undefined,
      employee: filters.employee?.length ? filters.employee[0] : undefined,
      changed_by: filters.changed_by?.length ? filters.changed_by[0] : undefined,
    })

    handleToast(
      toast,
      true,
      t('workforce.messages.exportSuccess'),
      t('workforce.messages.exportSuccessMessage', { format: 'CSV' }),
    )
  } catch (e) {
    console.error(e)
    handleToast(
      toast,
      false,
      t('workforce.messages.exportFailed'),
      t('workforce.messages.exportFailedMessage'),
    )
  } finally {
    setIsExporting(false)
    setIsExportDropdownOpen(false)
  }
}

  // --- Columns ---
  const columns: Column[] = useMemo(() => {
    return [
      {
        label: t('workforce.mutations.action', 'Action'),
        accessor: 'action',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const entry = row as BenefitChangeLogEntry
          const action = entry.action
          const actionColors = ACTION_BADGE_CONFIG[action as keyof typeof ACTION_BADGE_CONFIG] ?? ACTION_BADGE_CONFIG.default
          const actionIcon = action === 'create' ? Plus : action === 'update' ? Edit3 : action === 'delete' ? Trash2 : null
          const actionLabel = action === 'create' ? t('common.actions.create') : action === 'update' ? t('common.actions.update') : action === 'delete' ? t('common.actions.delete') : action
          const actionConfig = { ...actionColors, icon: actionIcon, label: actionLabel }
          
          const IconComponent = actionConfig.icon

          return (
            <Flex
              align="center"
              gap="6px"
              px="10px"
              h="28px"
              borderRadius="14px"
              bg={actionConfig.bg}
              color={actionConfig.color}
              borderWidth="1px"
              borderColor={actionConfig.borderColor}
              display="inline-flex"
              w="fit-content"
            >
              {IconComponent && <Icon as={IconComponent} boxSize="14px" strokeWidth={2.5} />}
              <Text fontSize="12px" fontWeight="600">
                {actionConfig.label}
              </Text>
            </Flex>
          )
        },
      },
      {
        label: t('common.fields.assignedTo'),
        accessor: 'assigned_to',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const entry = row as BenefitChangeLogEntry
          const benefit = typeof entry.benefit === 'object' ? entry.benefit : null
          
          if (!benefit) return <Text fontSize="12px" color="solid.400">—</Text>
          
          // Show employee if assigned to employee
          if (benefit.employee && !benefit.team && !benefit.country_of_work) {
            const name = `${benefit.employee.first_name || ''} ${benefit.employee.last_name || ''}`.trim()
            const profilePicture = benefit.employee.profile_picture

            return (
              <AvatarCell image={profilePicture} name={name || t('common.labels.na')} />
            )
          }
          
          // Show team if assigned to team
          if (benefit.team) {
            return (
              <Text fontSize="13px" fontWeight="500" color="solid.900" isTruncated>
                {benefit.team.name || t('common.labels.na')}
              </Text>
            )
          }
          
          // Show country if assigned to country
          if (benefit.country_of_work) {
            return (
              <Flex alignItems="center" gap="8px">
                <CircleFlag
                  countryCode={benefit.country_of_work.toLowerCase()}
                  style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                />
                <Text fontSize="13px" fontWeight="500" color="solid.900" isTruncated>
                  {getCountryNameByCode(benefit.country_of_work) || benefit.country_of_work}
                </Text>
              </Flex>
            )
          }
          
          return <Text fontSize="12px" color="solid.400">{t('common.labels.na')}</Text>
        },
      },
      {
        label: t('common.fields.type'),
        accessor: 'benefit_type',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const entry = row as BenefitChangeLogEntry
          const benefit = typeof entry.benefit === 'object' ? entry.benefit : null
          
          if (!benefit || !benefit.type) return <Text fontSize="12px" color="solid.400">—</Text>
          
          const fallback = BENEFIT_TYPE_FALLBACK[benefit.type] || benefit.type
          const label = t(`workforce.benefits.types.${benefit.type}`, fallback)
          const config = BENEFIT_TYPE_CONFIG[benefit.type] || BENEFIT_TYPE_CONFIG.other
          
          return (
            <Tooltip label={label} placement="top" hasArrow>
              <Flex
                align="center"
                gap="8px"
                px="12px"
                h="28px"
                minW={0}
                maxW="100%"
                borderRadius="14px"
                bg={config.bg}
                color={config.color}
                borderWidth="1px"
                borderColor={config.color}
                display="inline-flex"
                overflow="hidden"
              >
                <Icon as={config.icon} boxSize="16px" flexShrink={0} />
                <Text fontSize="13px" fontWeight="600" isTruncated>
                  {label}
                </Text>
              </Flex>
            </Tooltip>
          )
        },
      },
      {
        label: t('workforce.mutations.changeSummary', 'Change'),
        accessor: 'change_summary',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const entry = row as BenefitChangeLogEntry
          
          if (entry.action === 'create') {
            return (
              <Flex align="center" gap="6px" px="8px" h="28px" borderRadius="8px" bg={CHANGE_SUMMARY_BADGE.create.bg} w="fit-content">
                <Icon as={Plus} boxSize="12px" color={CHANGE_SUMMARY_BADGE.create.iconColor} strokeWidth={2.5} />
                <Text fontSize="12px" fontWeight="500" color={CHANGE_SUMMARY_BADGE.create.textColor}>
                  {t('workforce.mutations.benefitCreated', 'Benefit created')}
                </Text>
              </Flex>
            )
          }

          if (entry.action === 'delete') {
            return (
              <Flex align="center" gap="6px" px="8px" h="28px" borderRadius="8px" bg={CHANGE_SUMMARY_BADGE.delete.bg} w="fit-content">
                <Icon as={Trash2} boxSize="12px" color={CHANGE_SUMMARY_BADGE.delete.iconColor} strokeWidth={2.5} />
                <Text fontSize="12px" fontWeight="500" color={CHANGE_SUMMARY_BADGE.delete.textColor}>
                  {t('workforce.mutations.benefitDeleted', 'Benefit deleted')}
                </Text>
              </Flex>
            )
          }
          
          if (entry.action === 'update') {
            const fieldName = entry.field_name || 'Field'
            const prevValue = typeof entry.previous_value === 'string' ? entry.previous_value : '—'
            const newValue = typeof entry.new_value === 'string' ? entry.new_value : '—'
            
            return (
              <Tooltip 
                label={`${fieldName}: ${prevValue} → ${newValue}`}
                placement="top"
                hasArrow
              >
                <Flex align="center" gap="6px" maxW="300px">
                  <Text fontSize="12px" fontWeight="500" color="solid.600" flexShrink={0}>
                    {fieldName}:
                  </Text>
                  <Box minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    <Text as="span" fontSize="12px" fontWeight="500" color="solid.400">
                      {prevValue}
                    </Text>
                  </Box>
                  <Icon as={ArrowRight} boxSize="12px" color="solid.300" flexShrink={0} />
                  <Box minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    <Text as="span" fontSize="12px" fontWeight="500" color="solid.900">
                      {newValue}
                    </Text>
                  </Box>
                </Flex>
              </Tooltip>
            )
          }
          
          return <Text fontSize="12px" color="solid.400">—</Text>
        },
      },
      {
        label: t('common.fields.changedBy'),
        accessor: 'changed_by',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => {
          const entry = row as BenefitChangeLogEntry
          const changedBy = typeof entry.changed_by === 'object' ? entry.changed_by : null
          
          if (!changedBy) return <Text fontSize="12px" color="solid.400">—</Text>
          
          const name = `${changedBy.first_name || ''} ${changedBy.last_name || ''}`.trim()
          const profilePicture = changedBy.profile_picture

          return (
            <AvatarCell image={profilePicture} name={name || t('common.labels.na')} />
          )
        },
      },
      {
        label: t('common.fields.date'),
        accessor: 'created_at',
        type: 'custom',
        Cell: ({ row }: { row: RowData }) => (
          <Text textStyle="l-xs" color="solid.500">
            {(row as BenefitChangeLogEntry).created_at ? formatDate((row as BenefitChangeLogEntry).created_at) : '—'}
          </Text>
        ),
      },
    ]
  }, [t])

  return (
    <Box width="100%" height="100%" overflowY="auto">
      <Card width="100%" p="0">
        <Grid>
          <Box>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              height="64px"
              flexWrap="wrap"
              gap={4}
              px="20px"
              minW={0}
            >
              <SearchInput onSearch={(val) => { setSearchTerm(val); setPage(1) }} />

              <Flex gap="8px">
  <FilterButton
    label={t('common.fields.employee')}
    options={employeeFilterList}
    onApply={(selected) => updateFilter('employee', selected)}
    search={employeeFilterSearch}
    setSearch={setEmployeeFilterSearch}
    onLoadMore={() => {
      if (employeeFilterQuery.hasNextPage && !employeeFilterQuery.isFetchingNextPage) {
        employeeFilterQuery.fetchNextPage()
      }
    }}
    hasMore={!!employeeFilterQuery.hasNextPage}
    isLoadingMore={employeeFilterQuery.isFetchingNextPage}
  />

  <FilterButton
    label={t('common.fields.changedBy')}
    options={changedByFilterList}
    onApply={(selected) => updateFilter('changed_by', selected)}
    search={changedByFilterSearch}
    setSearch={setChangedByFilterSearch}
    onLoadMore={() => {
      if (changedByFilterQuery.hasNextPage && !changedByFilterQuery.isFetchingNextPage) {
        changedByFilterQuery.fetchNextPage()
      }
    }}
    hasMore={!!changedByFilterQuery.hasNextPage}
    isLoadingMore={changedByFilterQuery.isFetchingNextPage}
  />

  <Box position="relative">
    <Button
      variant="white"
      onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
      disabled={!mutationsList.length || isExporting}
      size="sm"
      h="32px"
      px="14px"
      fontSize="13px"
      fontWeight="600"
      borderRadius="8px"
    >
      <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="black" mr={2}>
        <ExportIcon />
      </Icon>
      {isExporting ? t('common.states.exporting') : t('workforce.people.exportAll')}
    </Button>

    {isExportDropdownOpen && (
      <Box
        position="absolute"
        right="0"
        width="196px"
        height="92px"
        borderRadius="8px"
        mt={2}
        boxShadow="md"
        bg="white"
        zIndex="1000"
      >
        <VStack align="stretch" spacing={0} height="100%">
          <Button
            variant="dropdown"
            height="50%"
            borderRadius="8px 8px 0 0"
            onClick={exportMutationsToXls}
            fontSize="sm"
          >
            {t('workforce.people.exportAsXLS')}
          </Button>
          <Button
            variant="dropdown"
            height="50%"
            borderRadius="0 0 8px 8px"
            onClick={exportMutationsToCsv}
            fontSize="sm"
          >
            {t('workforce.people.exportAsCSV')}
          </Button>
        </VStack>
      </Box>
    )}
  </Box>
</Flex>
            </Flex>

            {mutationsQuery.isLoading ? (
              <LoadingTable columns={columns} />
            ) : (
              <Box minHeight="58vh">
                <MasterTable
                  columns={columns}
                  data={mutationsList}
                  onRowClick={() => {}}
                  onOpenNewTab={() => {}}
                  selectedRows={[]}
                  onSelectAll={() => {}}
                  onSelectRow={() => {}}
                  allSelected={false}
                  isIndeterminate={false}
                  tableType={'people' as any}
                />
              </Box>
            )}

            <Pagination
              currentPage={page}
              itemsPerPage={itemsPerPage}
              maxPage={mutationsQuery.data?.max_page ?? Math.ceil((mutationsList.length || 1) / itemsPerPage)}
              totalItems={mutationsQuery.data?.count ?? mutationsList.length}
              handlePreviousPage={() => setPage(page - 1)}
              handleNextPage={() => setPage(page + 1)}
              handlePageClick={(p: number) => setPage(p)}
            />
          </Box>
        </Grid>
      </Card>
    </Box>
  )
}

export default BenefitMutationsPage
